import * as React from 'react'
// types
import { IField, IFieldGroup, IFieldLink } from 'types/graphql'
// context
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// utils
import {
  buildGroupingMap,
  GroupingMapItem
} from 'components/PageComponents/RiskTemplates/Edit/components/RiskTemplateFields/components/Table/components/TableForm/utils'

interface IContext {
  fieldLinks: IFieldLink[]
  rowHeights: Record<string, any>
  groupingMap: GroupingMapItem[]
  fieldGroups: IFieldGroup[]
  setRowHeight: (groupId: string, rowId: string, height: number) => void
  columnHeights: Record<string, any>
  removeRowHeight: (groupId: string, rowId: string) => void
  setColumnHeight: (column: IFieldLink, height: number) => void
  removeColumnHeight: (columnId: string) => void
}

interface IProps {
  field: IField
  children?: React.ReactNode
}

export const RiskTableContext = React.createContext<IContext>({} as any)

const RiskTableProvider: React.FC<IProps> = ({ children, field }) => {
  const { riskTemplate } = React.useContext(RiskTemplateAPIContext)

  const [rowHeights, setRowHeights] = React.useState<Record<string, any>>({})
  const [columnHeights, setColumnHeights] = React.useState<Record<string, any>>({})

  const fieldLinks = riskTemplate.fieldLinks.filter(({ parentFieldId }) => parentFieldId === field.id)
  const fieldGroups = riskTemplate.fieldGroups.filter(({ parentFieldId }) => parentFieldId === field.id)

  const groupingMap = React.useMemo(() => buildGroupingMap(riskTemplate, field.id), [riskTemplate, field.id])

  const setRowHeight: IContext['setRowHeight'] = React.useCallback((groupId, rowId, height) => {
    setRowHeights((prev) => {
      const group = prev[groupId] ?? {}
      const prevHeight = group[rowId] ?? 0

      if (prevHeight === height) {
        return prev
      }

      return {
        ...prev,
        [groupId]: { ...group, [rowId]: height }
      }
    })
  }, [])

  const removeRowHeight: IContext['removeRowHeight'] = React.useCallback((groupId, rowId) => {
    setRowHeights((prev) => {
      const group = prev[groupId]

      if (!group || !(rowId in group)) {
        return prev
      }

      const { [rowId]: _, ...rest } = group

      if (Object.keys(rest).length === 0) {
        const { [groupId]: __, ...next } = prev
        return next
      }

      return { ...prev, [groupId]: rest }
    })
  }, [])

  const setColumnHeight: IContext['setColumnHeight'] = React.useCallback((column, height) => {
    setColumnHeights((prev) => {
      const prevHeight = prev[column.id] ?? 0

      if (prevHeight === height) {
        return prev
      }

      return {
        ...prev,
        [column.id]: {
          height,
          position: column.position
        }
      }
    })
  }, [])

  const removeColumnHeight: IContext['removeColumnHeight'] = React.useCallback((columnId) => {
    setRowHeights((prev) => Object.fromEntries(Object.entries(prev).filter(([key]) => key !== columnId)))
  }, [])

  const value = {
    fieldLinks,
    rowHeights,
    fieldGroups,
    groupingMap,
    setRowHeight,
    columnHeights,
    setColumnHeight,
    removeRowHeight,
    removeColumnHeight
  }

  return <RiskTableContext.Provider value={value}>{children}</RiskTableContext.Provider>
}

export default RiskTableProvider
